import { ExportButton } from "@components/exports/ExportButton";
import { DrivePickerButton } from "@components/shared/DrivePickerButton";
import { FileDropZone } from "@components/shared/FileDropZone";
import {
  EXPORT_ASSESSMENT_FORM_TO_GOOGLE_DOCS,
  IMPORT_ASSESSMENT_FORM_RESPONSE,
} from "@lib/graphql/mutations";
import { ClipboardCheck, Upload } from "lucide-react";
import { useState } from "react";
import { useMutation } from "urql";

export interface AssessmentFormVersionData {
  id: string;
  versionNumber: number;
}

export interface AssessmentFormData {
  id: string;
  currentVersion: AssessmentFormVersionData | null;
}

interface AssessmentFormPanelProps {
  candidateSelectionStepId: string;
  form: AssessmentFormData | null;
  onImported?: () => void;
}

const ACCEPT_TYPES =
  ".txt,.md,.pdf,.docx,text/plain,text/markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function getCsrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";
}

export function AssessmentFormPanel({
  candidateSelectionStepId,
  form,
  onImported,
}: AssessmentFormPanelProps) {
  const [, exportToGoogleDocs] = useMutation(EXPORT_ASSESSMENT_FORM_TO_GOOGLE_DOCS);
  const [{ fetching: importing }, importResponse] = useMutation(IMPORT_ASSESSMENT_FORM_RESPONSE);
  const current = form?.currentVersion ?? null;

  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importDriveSignedId, setImportDriveSignedId] = useState<string | null>(null);
  const [importDriveFilename, setImportDriveFilename] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleExportStart = async () => {
    const result = await exportToGoogleDocs({ input: { candidateSelectionStepId } });
    const payload = result.data?.exportAssessmentFormToGoogleDocs;
    return {
      documentExportId: payload?.documentExport?.id ?? null,
      errors: payload?.errors ?? [],
    };
  };

  const resetImport = () => {
    setImportFile(null);
    setImportDriveSignedId(null);
    setImportDriveFilename(null);
    setImportError(null);
  };

  const closeImport = () => {
    setImportOpen(false);
    resetImport();
  };

  const handleImport = async () => {
    if (!importFile && !importDriveSignedId) {
      setImportError("Choose a file to import.");
      return;
    }
    setImportError(null);
    setUploading(true);
    try {
      let signedId: string;
      let originalFilename: string;
      if (importDriveSignedId) {
        signedId = importDriveSignedId;
        originalFilename = importDriveFilename ?? "assessment-form";
      } else {
        const formData = new FormData();
        formData.append("file", importFile as File);
        const uploadResponse = await fetch("/uploads", {
          method: "POST",
          headers: { "X-CSRF-Token": getCsrfToken() },
          body: formData,
        });
        if (!uploadResponse.ok) {
          throw new Error(`Upload failed (${uploadResponse.status})`);
        }
        const uploadData = (await uploadResponse.json()) as {
          signed_id: string;
          filename: string;
        };
        signedId = uploadData.signed_id;
        originalFilename = uploadData.filename;
      }
      const result = await importResponse({
        input: {
          candidateSelectionStepId,
          blobSignedId: signedId,
          originalFilename,
        },
      });
      const errors = result.data?.importAssessmentFormResponse?.errors ?? [];
      if (errors.length > 0) {
        setImportError(errors.join(", "));
        return;
      }
      closeImport();
      onImported?.();
    } catch (e) {
      setImportError(e instanceof Error ? e.message : "Import failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="mt-2 rounded border border-border bg-surface p-2"
      data-testid="assessment-form-panel"
    >
      <div className="flex flex-wrap items-center gap-2">
        <ClipboardCheck size={12} className="text-text-faint" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
          Assessment Form
        </span>
        {current && <span className="text-[11px] text-text-faint">v{current.versionNumber}</span>}

        <div className="ml-auto flex items-center gap-2">
          {current && (
            <ExportButton
              label="Export blank form to Google Docs"
              onStart={handleExportStart}
              className="flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
            />
          )}
          {current && (
            <button
              type="button"
              onClick={() => setImportOpen((open) => !open)}
              className="flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
              data-testid="import-assessment-form-toggle"
            >
              <Upload size={12} />
              Import filled form
            </button>
          )}
        </div>
      </div>

      {!current && (
        <p className="mt-1 text-[11px] text-text-faint">
          The blank form is built from the assessment matrix when the interview pack is generated.
          Generate the pack to enable export.
        </p>
      )}

      {importOpen && current && (
        <div className="mt-2 space-y-2 rounded border border-border bg-card-alt p-2">
          <p className="text-[11px] text-text-muted">
            Upload the interviewer's filled-in form (.docx, .pdf, or Google Doc). An LLM will
            extract the structured fields; anything that doesn't fit a template slot is preserved as
            residual notes.
          </p>
          <FileDropZone
            accept={ACCEPT_TYPES}
            disabled={uploading || importing}
            onFiles={(files) => {
              setImportFile(files[0] ?? null);
              setImportDriveSignedId(null);
              setImportDriveFilename(null);
              setImportError(null);
            }}
            overlayLabel="Drop filled form here"
            inputAriaLabel="Filled assessment form file"
          >
            {({ openPicker }) => (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={openPicker}
                    disabled={uploading || importing}
                    className="rounded border border-border bg-surface px-2 py-1 text-xs text-text-secondary hover:bg-card-hover disabled:opacity-50"
                  >
                    {importFile || importDriveSignedId ? "Change file" : "Choose file"}
                  </button>
                  <DrivePickerButton
                    accept={ACCEPT_TYPES}
                    disabled={uploading || importing}
                    onSignedId={(signedId, filename) => {
                      setImportFile(null);
                      setImportDriveSignedId(signedId);
                      setImportDriveFilename(filename);
                      setImportError(null);
                    }}
                  />
                  <span className="text-[10px] text-text-faint">
                    {importFile?.name ?? importDriveFilename ?? "No file chosen — or drop one here"}
                  </span>
                </div>
              </div>
            )}
          </FileDropZone>

          {importError && (
            <p className="text-[10px] text-red-600 dark:text-red-400">{importError}</p>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleImport}
              disabled={uploading || importing}
              data-testid="import-assessment-form-submit"
              className="rounded bg-accent px-3 py-1 text-xs text-white hover:bg-accent/90 disabled:opacity-50"
            >
              {uploading || importing ? "Importing…" : "Import"}
            </button>
            <button
              type="button"
              onClick={closeImport}
              className="rounded border border-border px-3 py-1 text-xs text-text-muted hover:bg-card-hover"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
